import { api } from './apiClient';

/**
 * The tax service — the ONLY place the UI learns what tax a document attracts.
 *
 * No form computes `amount * rate / 100` any more. The browser cannot see the
 * tax masters, the effective-dated rate versions, the company's registrations
 * or the place-of-supply rules, so anything it worked out for itself was a
 * guess that agreed with the server only by luck. Purchase orders defaulted
 * every line to 18%, sales orders let the user retype the rate, and the
 * Expense and Service Work Order forms asked the user to type the tax amount
 * in rupees.
 *
 * Every request here hits the same services/taxEngine.js the persisting
 * models use, so what a user sees while typing is what lands in the ledger.
 */

/** Which side of the transaction the company is on. */
export type TaxDirection = 'OUTWARD' | 'INWARD';

/** The determination outcome, mirroring the API's TAX_TREATMENT enum. */
export type TaxTreatment =
  | 'INTRA_STATE'
  | 'INTER_STATE'
  | 'EXPORT'
  | 'EXPORT_WITH_TAX'
  | 'SEZ'
  | 'IMPORT'
  | 'EXEMPT'
  | 'NIL_RATED'
  | 'NON_GST';

export type TaxTransactionType =
  | 'QUOTATION' | 'SALES_ORDER' | 'DELIVERY' | 'SALES_INVOICE' | 'SALES_RETURN' | 'CREDIT_NOTE'
  | 'PURCHASE_REQUISITION' | 'RFQ' | 'SUPPLIER_QUOTATION' | 'PURCHASE_ORDER'
  | 'GOODS_RECEIPT' | 'PURCHASE_INVOICE' | 'DEBIT_NOTE'
  | 'EXPENSE' | 'SERVICE_WORK_ORDER' | 'SERVICE_ENTRY'
  | 'SUBCONTRACT_ORDER' | 'JOB_WORK_INVOICE';

export interface TaxLineInput {
  productId?: number | null;
  hsnCode?: string | null;
  taxCategoryId?: number | null;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  description?: string | null;
  uom?: string | null;
  /** Only honoured when the request sets allowRateOverride. */
  gstRate?: number | null;
  isTaxInclusive?: boolean;
}

export interface TaxChargeInput {
  description?: string;
  amount: number;
  taxable?: boolean;
  gstRate?: number;
  taxCategoryId?: number | null;
  hsnCode?: string | null;
}

export interface TaxCalculationInput {
  transactionType: TaxTransactionType;
  partyId?: number | null;
  documentDate?: string | null;
  placeOfSupply?: string | null;
  isTaxInclusive?: boolean;
  isExport?: boolean;
  exportType?: string | null;
  isReverseCharge?: boolean | null;
  headerDiscountPercent?: number;
  headerDiscountAmount?: number;
  roundOff?: boolean;
  allowRateOverride?: boolean;
  lines: TaxLineInput[];
  charges?: TaxChargeInput[];
}

export interface TaxCalculatedLine {
  lineNumber: number;
  productId: number | null;
  productCode: string | null;
  productName: string | null;
  description: string | null;
  uom: string | null;
  hsnCode: string | null;
  quantity: number;
  unitPrice: number;
  grossAmount: number;
  discountPercent: number;
  discountAmount: number;
  headerDiscountShare: number;
  taxableAmount: number;
  taxCategoryId: number | null;
  taxCategoryCode: string | null;
  taxType: string;
  taxTreatment: TaxTreatment;
  gstRate: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessRate: number;
  cessAmount: number;
  totalTax: number;
  lineTotal: number;
  isTaxInclusive: boolean;
  itcEligible: boolean;
  /** Where the rate came from: product.taxCategory, hsn.gstRate, tenantDefault … */
  rateSource: string;
  rateOverridden: boolean;
}

export interface TaxCalculation {
  transactionType: string;
  direction: TaxDirection;
  documentDate: string;
  company: {
    gstin: string | null;
    stateCode: string | null;
    legalName: string | null;
    registrationType: string;
    registered: boolean;
    source: string;
  };
  party?: {
    kind: 'CUSTOMER' | 'VENDOR';
    id: number;
    name: string;
    gstin: string | null;
    stateCode: string | null;
    registrationType: string;
  } | null;
  placeOfSupply: string | null;
  taxTreatment: TaxTreatment;
  isInterState: boolean;
  isReverseCharge: boolean;
  isTaxInclusive: boolean;
  isExport: boolean;
  exportType: string | null;
  lines: TaxCalculatedLine[];
  charges: Array<{
    description: string;
    amount: number;
    taxable: boolean;
    hsnCode: string | null;
    gstRate: number;
    cgstAmount: number;
    sgstAmount: number;
    igstAmount: number;
    totalTax: number;
    lineTotal: number;
  }>;
  subtotal: number;
  discountAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessAmount: number;
  totalGst: number;
  totalTax: number;
  roundOffAmount: number;
  totalAmount: number;
  /** Under reverse charge this is the taxable value only — what the supplier is paid. */
  payableToParty: number;
  /** Plain-English reasons, safe to render verbatim under the tax panel. */
  explanation: string[];
}

export interface TaxPartyContext {
  company: TaxCalculation['company'];
  party: TaxCalculation['party'];
  placeOfSupply: string | null;
  taxTreatment: TaxTreatment;
  isInterState: boolean;
  isReverseCharge: boolean;
}

export interface TaxCategory {
  id: number;
  category_code: string;
  category_name: string;
  tax_type: 'GST' | 'EXEMPT' | 'NIL_RATED' | 'ZERO_RATED' | 'NON_GST';
  applies_to: 'GOODS' | 'SERVICES' | 'BOTH';
  reverse_charge: number;
  itc_eligible: number;
  is_default: number;
  is_active: number;
  description: string | null;
  gst_rate: number | null;
  cess_rate: number | null;
  cess_amount_per_unit: number | null;
  effective_from: string | null;
}

export interface GstState {
  state_code: string;
  state_name: string;
  is_union_territory: number;
}

export interface CompanyTaxRegistration {
  id: number;
  gstin: string;
  state_code: string;
  state_name?: string;
  legal_name: string | null;
  trade_name: string | null;
  address_line: string | null;
  city: string | null;
  pincode: string | null;
  registration_type: string;
  pan: string | null;
  is_default: number;
  is_active: number;
  effective_from: string | null;
}

export const taxService = {
  /** The whole determination for a set of lines. */
  calculate: (input: TaxCalculationInput): Promise<TaxCalculation> =>
    api.post<TaxCalculation>('/tax/calculate', input),

  /**
   * The determination inputs for a party, with no lines — so a form can say
   * "IGST will apply" the moment a customer is picked, before anything is
   * entered.
   */
  context: (params: {
    partyKind: 'CUSTOMER' | 'VENDOR';
    partyId?: number | null;
    placeOfSupply?: string | null;
    direction?: TaxDirection;
  }): Promise<TaxPartyContext> => api.get<TaxPartyContext>('/tax/context', { params }),

  categories: (onDate?: string): Promise<TaxCategory[]> =>
    api.get<TaxCategory[]>('/tax/categories', { params: onDate ? { onDate } : undefined }),

  states: (): Promise<GstState[]> => api.get<GstState[]>('/tax/states'),

  registrations: (): Promise<{ registrations: CompanyTaxRegistration[]; resolved: TaxCalculation['company'] }> =>
    api.get('/tax/registrations'),

  createRegistration: (data: Partial<CompanyTaxRegistration> & { gstin: string }): Promise<{ id: number }> =>
    api.post('/tax/registrations', data),

  updateRegistration: (id: number, data: Partial<CompanyTaxRegistration>): Promise<{ id: number }> =>
    api.put(`/tax/registrations/${id}`, data),

  createCategory: (data: {
    categoryCode: string; categoryName: string; taxType?: string; appliesTo?: string;
    reverseCharge?: boolean; itcEligible?: boolean; description?: string;
    gstRate?: number; cessRate?: number; cessAmountPerUnit?: number; effectiveFrom?: string;
  }): Promise<{ id: number }> => api.post('/tax/categories', data),

  addRateVersion: (categoryId: number, data: {
    gstRate: number; cessRate?: number; cessAmountPerUnit?: number;
    effectiveFrom: string; notificationRef?: string;
  }): Promise<{ id: number }> => api.post(`/tax/categories/${categoryId}/rates`, data),
};

/** Human label for a treatment, for chips and summary strips. */
export const taxTreatmentLabel = (t: TaxTreatment | null | undefined): string => {
  switch (t) {
    case 'INTRA_STATE': return 'CGST + SGST';
    case 'INTER_STATE': return 'IGST';
    case 'SEZ': return 'SEZ — IGST';
    case 'EXPORT': return 'Export — zero rated';
    case 'EXPORT_WITH_TAX': return 'Export with IGST';
    case 'IMPORT': return 'Import — IGST at customs';
    case 'EXEMPT': return 'Exempt';
    case 'NIL_RATED': return 'Nil rated';
    case 'NON_GST': return 'Non-GST';
    default: return '—';
  }
};

export default taxService;
